import { FC, useMemo } from "react";
import { Button, Card, Empty, Table, Tag } from "antd";
import { Package, Ruler } from "lucide-react";
import { useEstimateDetailStore } from "../estimate-detail.store";
import { EstimateTicket } from "~/types/estimate";
import { InventoryTemplate } from "~/services/inventory-templates/inventory-templates.types";
import { PackingCalculator } from "../packing-calculator";
import { PackingTypes } from "~/utils/constants";

interface InventoryTabProps {
  estimate?: EstimateTicket;
}

export const InventoryTab: FC<InventoryTabProps> = ({ estimate }) => {
  const { setShowInventoryModal } = useEstimateDetailStore();

  // Collect all inventory templates from all services
  const allInventoryItems = useMemo(() => {
    if (!estimate?.services) return [];

    const items: InventoryTemplate[] = [];
    estimate.services.forEach((service) => {
      if (service.inventoryTemplates) {
        items.push(...service.inventoryTemplates);
      }
    });

    return items;
  }, [estimate?.services]);

  // Summary stats
  const stats = useMemo(() => {
    const loose = allInventoryItems.filter(
      (item) => !item.packingType || item.packingType === PackingTypes.LOOSE
    ).length;
    const packed = allInventoryItems.filter(
      (item) => item.packingType && item.packingType !== PackingTypes.LOOSE
    ).length;

    return { total: allInventoryItems.length, loose, packed };
  }, [allInventoryItems]);

  if (!estimate) return null;

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: InventoryTemplate) => (
        <div>
          <div className="font-medium">{name}</div>
          {record.description && (
            <div className="text-xs text-gray-500">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_: any, record: InventoryTemplate) => (
        <span className="flex items-center text-sm">
          <Ruler size={14} className="mr-1 text-gray-400" />
          {record.length && record.width && record.height
            ? `${record.length}×${record.width}×${record.height}"`
            : "—"}
        </span>
      ),
    },
    {
      title: "Medium",
      dataIndex: "medium",
      key: "medium",
      render: (medium: string) => medium || "—",
    },
    {
      title: "Packing",
      dataIndex: "packingType",
      key: "packingType",
      render: (type: PackingTypes) => {
        if (!type || type === PackingTypes.LOOSE) {
          return <Tag color="orange">LOOSE</Tag>;
        }
        return <Tag color="purple">{type}</Tag>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Estimated Inventory</h2>
          <p className="text-gray-500 text-sm">
            {stats.total} items total • {stats.loose} loose • {stats.packed} packed
          </p>
        </div>
        <Button
          type="primary"
          icon={<Package size={16} />}
          onClick={() => setShowInventoryModal(true)}
        >
          Add from Templates
        </Button>
      </div>

      {allInventoryItems.length > 0 ? (
        <>
          {/* Inventory List */}
          <Card size="small">
            <Table
              columns={columns}
              dataSource={allInventoryItems}
              rowKey="id"
              size="small"
              pagination={allInventoryItems.length > 10 ? { pageSize: 10 } : false}
            />
          </Card>

          {/* Packing Calculator - only show if there are loose items */}
          {stats.loose > 0 && (
            <PackingCalculator
              inventoryItems={allInventoryItems}
              onCalculationComplete={(results) => {
                console.log("Packing calculation results:", results);
                // TODO: Save results to estimate or use for billing
              }}
            />
          )}
        </>
      ) : (
        <Empty
          image={<Package size={48} className="text-gray-300 mx-auto" />}
          description="No inventory items yet"
          className="my-8"
        >
          <p className="text-sm text-gray-500 mb-4">
            Add inventory templates to estimate packing costs
          </p>
          <Button
            type="primary"
            icon={<Package size={16} />}
            onClick={() => setShowInventoryModal(true)}
          >
            Browse Templates
          </Button>
        </Empty>
      )}
    </div>
  );
};
